import { useMemo, type ComponentProps } from 'react';
import { ContributionDetailsForm } from '../../components/contributions/ContributionDetailsForm';
import {
  buildRoadDetailsFormProps,
  type RoadDetailsSectionInput,
} from './RoadDetailsSection';

type BaseFormProps = ComponentProps<typeof ContributionDetailsForm>;
type RoadPropKeys = keyof ReturnType<typeof buildRoadDetailsFormProps>;

export type RoadContributionDetailsFormProps = Omit<BaseFormProps, RoadPropKeys> &
  RoadDetailsSectionInput;

/** Contribution details form with road survey props wired from ``RoadDetailsSectionInput``. */
export function RoadContributionDetailsForm({
  details,
  motorRoadSurveyFlow,
  selectedCategoryMeta,
  singleStripCaptureMode,
  onSingleStripCaptureModeChange,
  stripACaptureMode,
  onStripACaptureModeChange,
  stripBCaptureMode,
  onStripBCaptureModeChange,
  divideWidthM,
  onDivideWidthMChange,
  dividedDivideReady,
  dividedCarriagewayAReady,
  dividedCarriagewayBReady,
  dividedCarriagewayReady,
  stripATraceReady,
  stripBTraceReady,
  singleStripTraceReady,
  onStartMarkingDivide,
  onStartMarkingStripA,
  onStartMarkingStripB,
  onStartMarkingSingleStrip,
  dividedSurveySetupHint,
  onDismissDividedSurveySetupHint,
  roadGeometryStatusRows,
  onEditRoadGeometry,
  onRoadCarriagewayCountChange,
  ...formProps
}: RoadContributionDetailsFormProps) {
  const roadDetailsFormProps = useMemo(
    () =>
      buildRoadDetailsFormProps({
        details,
        motorRoadSurveyFlow,
        selectedCategoryMeta,
        singleStripCaptureMode,
        onSingleStripCaptureModeChange,
        stripACaptureMode,
        onStripACaptureModeChange,
        stripBCaptureMode,
        onStripBCaptureModeChange,
        divideWidthM,
        onDivideWidthMChange,
        dividedDivideReady,
        dividedCarriagewayAReady,
        dividedCarriagewayBReady,
        dividedCarriagewayReady,
        stripATraceReady,
        stripBTraceReady,
        singleStripTraceReady,
        onStartMarkingDivide,
        onStartMarkingStripA,
        onStartMarkingStripB,
        onStartMarkingSingleStrip,
        dividedSurveySetupHint,
        onDismissDividedSurveySetupHint,
        roadGeometryStatusRows,
        onEditRoadGeometry,
        onRoadCarriagewayCountChange,
      }),
    [
      details,
      motorRoadSurveyFlow,
      selectedCategoryMeta,
      singleStripCaptureMode,
      onSingleStripCaptureModeChange,
      stripACaptureMode,
      onStripACaptureModeChange,
      stripBCaptureMode,
      onStripBCaptureModeChange,
      divideWidthM,
      onDivideWidthMChange,
      dividedDivideReady,
      dividedCarriagewayAReady,
      dividedCarriagewayBReady,
      dividedCarriagewayReady,
      stripATraceReady,
      stripBTraceReady,
      singleStripTraceReady,
      onStartMarkingDivide,
      onStartMarkingStripA,
      onStartMarkingStripB,
      onStartMarkingSingleStrip,
      dividedSurveySetupHint,
      onDismissDividedSurveySetupHint,
      roadGeometryStatusRows,
      onEditRoadGeometry,
      onRoadCarriagewayCountChange,
    ],
  );

  return <ContributionDetailsForm {...formProps} {...roadDetailsFormProps} />;
}
